/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useEffect, useState } from 'react';
import { VideoProviderType } from '../types';
import { diagnostics } from '@utils/diagnostics';

interface ClosedCaptioningState {
  /** Show/hide closed captioning button if supported */
  isSupported: boolean;
  /** Show the closed captions if enabled */
  isEnabled: boolean;
}

/**
 * Hook to manage closed captioning
 **/
export function useClosedCaptioningSupport(videoProvider: VideoProviderType) {
  const [closedCaptions, setClosedCaptions] = useState<ClosedCaptioningState>({
    isSupported: false,
    isEnabled: false,
  });

  const toggleClosedCaptioning = () => {
    setClosedCaptions(prevState => ({
      ...prevState,
      isEnabled: !prevState.isEnabled,
    }));
  };

  useEffect(() => {
    if (closedCaptions.isEnabled) {
      try {
        void videoProvider.startClosedCaptioning();
      } catch (err: unknown) {
        diagnostics.error(new Error('Failed to start closed captioning'), { err });
      }
    }
  }, [closedCaptions.isEnabled]);

  useEffect(() => {
    setClosedCaptions(prevState => ({
      ...prevState,
      isSupported: videoProvider.isClosedCaptioningSupported(),
    }));
  }, [videoProvider]);

  return { closedCaptions, toggleClosedCaptioning };
}
